import { toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  type ShopStaffListResult,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopStaffFilter } from "@/modules/merchant/presentation/ui/ShopStaffFilter";
import { ShopStaffFormModal } from "@/modules/merchant/presentation/ui/ShopStaffFormModal";
import { ShopStaffTable } from "@/modules/merchant/presentation/ui/ShopStaffTable";
import { ShopStaffToolbar } from "@/modules/merchant/presentation/ui/ShopStaffToolbar";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import type { ShopStaffIndexSearchDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  shopStaffKeys,
  useDeleteShopStaff,
  useShopRolesQuery,
  useShopStaffQuery,
} from "../api/queries";

type StaffRow = ShopStaffListResult["data"][number];

export function ShopStaffPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: "/app/shops/$shopId/staff" });
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editStaff, setEditStaff] = useState<StaffRow | null>(null);

  const search = useSearch({
    from: "/app/shops/$shopId/staff",
    strict: false,
  }) as ShopStaffIndexSearchDTO;

  useEffect(() => {
    if (!search.assignBranchId || !shopId) return;
    setEditStaff(null);
    setModalOpen(true);
  }, [search.assignBranchId, shopId]);

  const list = useShopStaffQuery(shopId, {
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const shopRolesQuery = useShopRolesQuery(shopId);
  const shopRoles = shopRolesQuery.data ?? [];
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;

  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_staff.create,
    verifiedLevel,
  );
  const canUpdate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_staff.update,
    verifiedLevel,
  );
  const canDelete = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_staff.delete,
    verifiedLevel,
  );

  const deleteShopStaff = useDeleteShopStaff(shopId);
  const items = list.data?.data ?? [];
  const meta = list.data?.meta;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.shop_staff.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.staff(shopId)} />
      </Header>

      <Main>
        <ShopStaffToolbar
          canCreate={canCreate}
          onCreate={() => {
            setEditStaff(null);
            setModalOpen(true);
          }}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ShopStaffFilter
            shopRoles={shopRoles.map((r) => ({ id: r.id, name: r.name }))}
          />
          <ShopStaffTable
            data={items}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              shopId &&
              nav({
                to: "/app/shops/$shopId/staff",
                params: { shopId },
                search: { ...search, offset, limit },
              })
            }
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onSortingChange={(id, desc) =>
              shopId &&
              nav({
                to: "/app/shops/$shopId/staff",
                params: { shopId },
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(staff) => {
              setEditStaff(staff);
              setModalOpen(true);
            }}
            onDelete={(staff) =>
              toast.promise(deleteShopStaff.runForUser(staff.userId), {
                loading: "ກຳລັງລົບ...",
                success: "ລົບພະນັກງານສຳເລັດ",
                error: "ລົບພະນັກງານລົ້ມເຫຼວ",
              })
            }
            canUpdate={canUpdate}
            canDelete={canDelete}
          />
        </div>

        {shopId && (
          <ShopStaffFormModal
            open={modalOpen}
            onOpenChange={(open) => {
              setModalOpen(open);
              if (!open) {
                setEditStaff(null);
                if (search.assignBranchId) {
                  const { assignBranchId: _omit, ...rest } = search;
                  void nav({
                    to: "/app/shops/$shopId/staff",
                    params: { shopId },
                    search: rest,
                    replace: true,
                  });
                }
              }
            }}
            shopId={shopId}
            mode={editStaff ? "edit" : "create"}
            staff={editStaff ?? undefined}
            initialBranchId={editStaff ? null : (search.assignBranchId ?? null)}
            onSuccess={() => {
              qc.invalidateQueries({ queryKey: shopStaffKeys.all(shopId) });
            }}
          />
        )}
      </Main>
    </RequireMerchantPermission>
  );
}
